import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import * as roomActions from 'features/room-builder/duck';
import * as taskActions from 'features/task-management/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import Modal from 'react-modal';
import { mapTypeOptions, mapTimingOptions } from 'shared/helpers';
import { MultiRadio, AlertNotice, Modal as WarningModal } from 'shared/components';
import './RoomTypeAndTiming.scss';
import { isEmpty } from 'lodash';

const getSelectedType = (roomType, types) => types.find((t) => t.id === roomType.id) || {};

class RoomTypeAndTiming extends Component {
  constructor(props) {
    super(props);

    this.state = {
      room: props.room,
      errors: {},
      mismatchModalOpen: false,
      roomTypeOptions: mapTypeOptions(
        props.roomTypes,
        props.room.room?.roomType.onlineFlag,
        this.props?.tcEvent?.studyFlag
      ),
      timingOptions: [],
      timingWarningModalOpen: false,
    };

    this.onRoomTypeChange = this.onRoomTypeChange.bind(this);
    this.onTimingChange = this.onTimingChange.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.onUpdateRoomTypeTiming = this.onUpdateRoomTypeTiming.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
    this.closeMismatchModal = this.closeMismatchModal.bind(this);
    this.closeTimingWarningModal = this.closeTimingWarningModal.bind(this);
  }

  componentDidMount() {
    this.props.actions.getRoomTypes(this.props.id);
  }

  componentDidUpdate(prevProps) {
    const { roomTypeOptions } = this.state;

    // Handle room or roomTypes changes
    if (
      (isEmpty(this.state.room) && !isEmpty(this.props.room)) ||
      (!isEmpty(this.state.room) &&
        !isEmpty(this.props.room) &&
        this.state.room.id !== this.props.room.id) ||
      (isEmpty(roomTypeOptions) &&
        this.props.roomTypes.length &&
        prevProps.roomTypes !== this.props.roomTypes)
    ) {
      this.setState({
        room: this.props.room,
        roomTypeOptions: mapTypeOptions(
          this.props.roomTypes,
          this.props.room?.roomType ? this.props.room?.roomType.onlineFlag : false,
          this.props.editType,
          this.props?.tcEvent?.studyFlag
        ),
      });
    }

    // Handle modalOpen changes
    if (this.props.modalOpen && this.props.modalOpen !== prevProps.modalOpen) {
      this.setState({
        roomTypeOptions: mapTypeOptions(
          this.props.roomTypes,
          this.props.room?.roomType ? this.props.room?.roomType.onlineFlag : false,
          this.props.editType,
          this.props?.tcEvent?.studyFlag
        ),
        timingOptions: mapTimingOptions(
          this.props.roomTypes,
          this.props.mappedRoomTiming,
          this.props.room.roomType ? this.props.room.roomType.id : this.props.room.id
        ),
      });
    }
  }

  onTimingChange(e) {
    const { value } = e.target;
    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        timing: value,
      },
    }));
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  onRoomTypeChange(e) {
    const { value } = e.target;
    const type = this.props.roomTypes.find((type) => type.id === +value);

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        roomType: type,
      },
      errors: {
        ...prevState.errors,
        type: '',
      },
    }));
  }

  async onUpdateRoomTypeTiming(e, skipTimingCheck, skipRoomMismatchCheck) {
    const { room } = this.state;
    const { actions, roomTypes, editType } = this.props;
    if (editType && !room.roomType) {
      this.setState({
        errors: {
          type: ['errors.roomTypeRequired'],
        },
      });
      return;
    }

    if (!skipTimingCheck) {
      if (this.props.room.timing !== room.timing) {
        this.setState({
          timingWarningModalOpen: true,
        });
        return;
      }
    }

    if (!skipRoomMismatchCheck) {
      const mismatch = await actions.checkRoomMismatch(room);
      if (mismatch) {
        this.setState({
          mismatchModalOpen: true,
          timingWarningModalOpen: false,
        });
        return;
      }
    }

    const dataTypeTiming = {
      timing: room.timing,
      type: room.roomType,
    };
    actions.updateRoomTypeTiming(room.id, dataTypeTiming).then(() => {
      actions.setRoomStatusAction({
        data: {
          roomTiming: dataTypeTiming.timing,
          roomType: room.roomType && getSelectedType(room.roomType, roomTypes).name,
        },
      });
      this.onCloseModal(true);
    });
  }

  onCloseModal(redirect) {
    this.setState({
      errors: {},
      room: {},
      mismatchModalOpen: false,
      timingWarningModalOpen: false,
    });

    this.props.closeModal(redirect);
  }

  closeMismatchModal() {
    this.setState({
      mismatchModalOpen: false,
    });
  }

  closeTimingWarningModal() {
    this.setState({
      timingWarningModalOpen: false,
    });
  }

  render() {
    const { room, errors, timingWarningModalOpen } = this.state;
    const { location, editType, modalOpen, alertMessage, alertMessageType } = this.props;

    return (
      <div>
        <Modal
          isOpen={modalOpen}
          className="custom-modal modal-type-and-timing"
          shouldCloseOnOverlayClick={false}
          ariaHideApp={false}
          onRequestClose={this.toggleModal}>
          <div className="type-timing-container">
            {alertMessage && (
              <AlertNotice message={this.props.t(alertMessage)} type={alertMessageType} />
            )}
            <h5 className="fw-bold">{room.name}</h5>
            <p className="mb-4">
              <span>{location.name}</span>
              {location.area && location.area.length > 0 && <span>, {location.area}</span>}
            </p>

            {editType ? (
              <div>
                <div>
                  <MultiRadio
                    radioList={this.state.roomTypeOptions}
                    checkedValue={room.roomType ? room.roomType.id : ''}
                    onChange={this.onRoomTypeChange}
                    title={this.props.t('roomBuilder.roomTypeLabel')}
                    errors={errors.type}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-30">
                <MultiRadio
                  radioList={this.state.timingOptions}
                  checkedValue={room.timing}
                  onChange={this.onTimingChange}
                  title={this.props.t('taskManagement.roomTimingLabel')}
                  errors={errors.type}
                />
              </div>
            )}
            <div className="button-section">
              <button className="btn btn-outline" onClick={() => this.onCloseModal(false)}>
                {this.props.t('common.labels.cancel')}
              </button>
              <button className="btn btn-action" onClick={this.onUpdateRoomTypeTiming}>
                {this.props.t('common.labels.save')}
              </button>
            </div>
          </div>
        </Modal>
        <WarningModal
          closeModal={this.closeMismatchModal}
          hideIcon
          onConfirm={(e) => this.onUpdateRoomTypeTiming(e, true, true)}
          open={this.state.mismatchModalOpen}
          modalContent="roomBuilder.mismatchModalWarning"
        />
        <WarningModal
          closeModal={this.closeTimingWarningModal}
          hideIcon
          onConfirm={(e) => this.onUpdateRoomTypeTiming(e, true)}
          open={timingWarningModalOpen}
          modalContent="roomBuilder.timingWarningModal"
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    roomStatus: state.taskManagement.roomStatus,
    roomTypes: state.statics.roomTypes,
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...roomActions, ...taskActions, ...staticActions }, dispatch),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(RoomTypeAndTiming));

RoomTypeAndTiming.propTypes = {
  room: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    timing: PropTypes.string,
    roomType: PropTypes.shape({
      id: PropTypes.number,
      onlineFlag: PropTypes.bool,
    }),
  }).isRequired,
  modalOpen: PropTypes.bool.isRequired,
  editType: PropTypes.bool,
  alertMessage: PropTypes.string,
  alertMessageType: PropTypes.string,
  tcEvent: PropTypes.shape({
    studyFlag: PropTypes.bool,
  }).isRequired,
  actions: PropTypes.shape({
    getRoomTypes: PropTypes.func,
    checkRoomMismatch: PropTypes.func,
    updateRoomTypeTiming: PropTypes.func,
    setRoomStatusAction: PropTypes.func,
  }).isRequired,
  roomTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      onlineFlag: PropTypes.bool,
    })
  ),
  mappedRoomTiming: PropTypes.shape({}),
  id: PropTypes.number,
  location: PropTypes.shape({
    name: PropTypes.string,
    area: PropTypes.string,
  }),
  closeModal: PropTypes.func.isRequired,
  roomStatus: PropTypes.shape({}),
  t: PropTypes.func.isRequired,
};

RoomTypeAndTiming.defaultProps = {
  editType: false,
  alertMessage: '',
  alertMessageType: 'info',
};
